/* ═══════════════════════════════════════════════════════
   GAMOLUTION — Living Observatory Design System
   ═══════════════════════════════════════════════════════ */

/* ─── CSS Variables ─── */
:root {
  /* Backgrounds — deep, layered */
  --bg-void: #08090a;
  --bg-surface: #0f1114;
  --bg-elevated: #181b20;
  --bg-overlay: rgba(0, 0, 0, 0.7);

  /* The World — warm, alive, organic */
  --sugar-empty: #1a1610;
  --sugar-low: #3d2e0a;
  --sugar-mid: #7a5c14;
  --sugar-high: #c9a21e;
  --sugar-peak: #f5d442;
  --agent-alive: #4a9eff;
  --agent-deep: #7c5cff;
  --agent-dead: #2a1a1a;

  /* Data & Metrics — cool, precise */
  --metric-primary: #e8eaed;
  --metric-secondary: #9aa0a6;
  --metric-accent: #4a9eff;
  --metric-danger: #ff6b6b;
  --metric-success: #51cf66;

  /* Accents */
  --accent-gold: #c9a21e;
  --accent-blue: #4a9eff;
  --border-subtle: rgba(255, 255, 255, 0.06);
  --glow-sugar: 0 0 20px rgba(201, 162, 30, 0.15);
  --glow-agent: 0 0 12px rgba(74, 158, 255, 0.2);

  /* Typography */
  --font-display: 'Space Grotesk', sans-serif;
  --font-body: 'IBM Plex Sans', sans-serif;
  --font-mono: 'IBM Plex Mono', monospace;
}

/* ─── Reset ─── */
*,
*::before,
*::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

/* ─── Body ─── */
body {
  margin: 0;
  padding: 0;
  background: var(--cn-app, var(--bg-void));
  color: var(--cn-ink, var(--metric-primary));
  font-family: var(--font-body);
  font-size: 14px;
  line-height: 1.5;
  overflow: hidden;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* ─── Root ─── */
#root {
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
}

/* ─── Typography Utilities ─── */
.font-display {
  font-family: var(--font-display);
}

.font-body {
  font-family: var(--font-body);
}

.font-mono {
  font-family: var(--font-mono);
}

/* ─── App Layout ─── */
.app {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-width: 1200px;
}

/* ─── Header Bar ─── */
.app-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 44px;
  padding: 0 20px;
  background: var(--bg-surface);
  border-bottom: 1px solid var(--border-subtle);
  flex-shrink: 0;
  z-index: 10;
}

.app-title {
  font-family: var(--font-display);
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.12em;
  color: var(--metric-primary);
  margin: 0;
  text-transform: uppercase;
  user-select: none;
}

.app-world-name {
  font-family: var(--font-body);
  font-size: 13px;
  font-weight: 500;
  color: var(--metric-secondary);
  letter-spacing: 0.02em;
}

.app-world-picker {
  font-family: var(--font-mono);
  font-size: 12px;
  background: var(--bg-elevated);
  color: var(--metric-primary);
  border: 1px solid var(--border-subtle);
  padding: 4px 8px;
  border-radius: 2px;
  outline: none;
  cursor: pointer;
}

.app-world-picker:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.app-world-picker:focus {
  border-color: var(--accent-blue);
}

/* ─── Main Area ─── */
.app-main {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

/* World View area — the hero, takes remaining space */
.app-world {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: var(--bg-void);
  position: relative;
}

/* Right sidebar */
.app-sidebar {
  width: 280px;
  flex-shrink: 0;
  background: var(--bg-surface);
  border-left: 1px solid var(--border-subtle);
  overflow-y: auto;
  overflow-x: hidden;
}

/* Sidebar scrollbar */
.app-sidebar::-webkit-scrollbar {
  width: 4px;
}

.app-sidebar::-webkit-scrollbar-track {
  background: transparent;
}

.app-sidebar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.08);
  border-radius: 2px;
}

.app-sidebar::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.14);
}

/* ─── Header Right ─── */

.app-header-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.app-population {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--agent-alive);
  letter-spacing: 0.02em;
}

.app-wake-lock {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: var(--font-display);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--metric-success);
  padding: 3px 8px;
  border: 1px solid rgba(81, 207, 102, 0.25);
  border-radius: 2px;
  background: rgba(81, 207, 102, 0.06);
}

.app-wake-lock-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--metric-success);
  box-shadow: 0 0 6px rgba(81, 207, 102, 0.6);
  animation: app-wake-pulse 2.4s ease-in-out infinite;
}

@keyframes app-wake-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
}

/* ─── Deaths Toggle ─── */

.app-deaths-toggle {
  position: absolute;
  bottom: 24px;
  left: 180px;
  font-family: var(--font-display);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.04em;
  padding: 3px 10px;
  border: 1px solid var(--border-subtle);
  border-radius: 2px;
  background: rgba(8, 9, 10, 0.8);
  color: var(--metric-secondary);
  cursor: pointer;
  z-index: 5;
  transition: color 0.15s ease, border-color 0.15s ease;
}

.app-deaths-toggle:hover {
  color: var(--metric-primary);
  border-color: rgba(255, 255, 255, 0.15);
}

.app-deaths-toggle--active {
  color: #ff6b6b;
  border-color: rgba(255, 107, 107, 0.3);
}

/* ─── Save Button ─── */

.app-save-btn {
  font-family: var(--font-display);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  padding: 4px 12px;
  border: 1px solid var(--border-subtle);
  border-radius: 2px;
  background: transparent;
  color: var(--metric-secondary);
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease;
}

.app-save-btn:hover:not(:disabled) {
  color: var(--metric-primary);
  border-color: rgba(255, 255, 255, 0.15);
}

.app-save-btn:disabled {
  opacity: 0.3;
  cursor: default;
}

.app-save-btn--saved {
  color: var(--metric-success);
  border-color: var(--metric-success);
}

/* ─── Selection ─── */
::selection {
  background: rgba(201, 162, 30, 0.25);
  color: var(--metric-primary);
}
